import { diffDays, weekdayOnOrAfter, addWeeks } from "./dates";
import type { GuestPresenter, ISODate, Member, PresenterRef, Session } from "./types";

export type SessionPhase = "upcoming" | "today" | "overdue" | "completed" | "cancelled";

export function sessionPhase(session: Session, today: ISODate): SessionPhase {
  if (session.status === "completed") return "completed";
  if (session.status === "cancelled") return "cancelled";
  const diff = diffDays(session.date, today);
  if (diff > 0) return "upcoming";
  if (diff === 0) return "today";
  return "overdue";
}

export type SessionAction =
  | "editTopic"
  | "change"
  | "cancel"
  | "markDone"
  | "replacement"
  | "markCancelled"
  | "addSummary"
  | "logExtra"
  | "viewSummary"
  | "editSummary";

/** Plan table "Kondisi sesi → Aksi yang tampil". */
export function sessionActions(session: Session, today: ISODate, hasSummary: boolean): SessionAction[] {
  switch (sessionPhase(session, today)) {
    case "upcoming":
      return ["editTopic", "change", "cancel"];
    case "today":
      return ["markDone", "replacement", "change"];
    case "overdue":
      return ["markDone", "markCancelled"];
    case "completed":
      if (hasSummary) return ["viewSummary", "editSummary"];
      return session.type === "spontaneous" ? ["addSummary"] : ["addSummary", "logExtra"];
    case "cancelled":
      return [];
  }
}

/** A session that occupies its date (cancelled and spontaneous sessions do not). */
export function isBlocking(session: Session): boolean {
  return session.status !== "cancelled" && session.type !== "spontaneous";
}

export function sortByDate<T extends { date: ISODate }>(items: T[], dir: "asc" | "desc" = "asc"): T[] {
  return [...items].sort((a, b) => (dir === "asc" ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)));
}

/** The blocking session on a given date, if any (Rule 12 / Case J). */
export function occupantOf(sessions: Session[], date: ISODate, ignoreId?: string): Session | undefined {
  return sessions.find((s) => s.date === date && isBlocking(s) && s.id !== ignoreId);
}

export function sameRef(a: PresenterRef | undefined, b: PresenterRef | undefined): boolean {
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === "free" && b.kind === "free") return a.name.trim().toLowerCase() === b.name.trim().toLowerCase();
  return (a as { id: string }).id === (b as { id: string }).id;
}

export function refKey(ref: PresenterRef): string {
  return ref.kind === "free" ? `free:${ref.name.trim().toLowerCase()}` : `${ref.kind}:${ref.id}`;
}

/** Upcoming scheduled session of a member (Rule 11 / Case K: one active schedule per member). */
export function activeScheduleOf(sessions: Session[], memberId: string, today: ISODate): Session | undefined {
  return sortByDate(sessions).find(
    (s) =>
      s.status === "scheduled" &&
      s.type !== "spontaneous" &&
      s.presenter.kind === "member" &&
      s.presenter.id === memberId &&
      diffDays(s.date, today) >= 0,
  );
}

export function sessionsOfMember(sessions: Session[], memberId: string): Session[] {
  return sessions.filter((s) => s.presenter.kind === "member" && s.presenter.id === memberId);
}

export function thisWeekDate(today: ISODate, weekday: number): ISODate {
  return weekdayOnOrAfter(today, weekday);
}

export function sessionOn(sessions: Session[], date: ISODate): Session | undefined {
  return occupantOf(sessions, date);
}

export function nextWeekDate(today: ISODate, weekday: number): ISODate {
  return addWeeks(thisWeekDate(today, weekday), 1);
}

/** Display numbering "#43": position among all regular sessions (incl. cancelled) by date. */
export function sessionNumberMap(sessions: Session[]): Map<string, number> {
  const regular = sortByDate(sessions.filter((s) => s.type !== "spontaneous"));
  const map = new Map<string, number>();
  regular.forEach((s, i) => map.set(s.id, i + 1));
  return map;
}

export interface PresenterDirectory {
  members: Map<string, Member>;
  guests: Map<string, GuestPresenter>;
}

export function presenterName(ref: PresenterRef | undefined, dir: PresenterDirectory, style: "full" | "short" = "full"): string {
  if (!ref) return "—";
  if (ref.kind === "free") return ref.name;
  if (ref.kind === "guest") return dir.guests.get(ref.id)?.name ?? "Guest presenter";
  const m = dir.members.get(ref.id);
  if (!m) return "Former member";
  return style === "short" ? m.nickname : m.name;
}

/** Normalised topic title for counting unique topics. */
export function topicKey(topic: string): string {
  return topic
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
